import React, { useEffect, useRef, useState } from "react";

function RunnerLeg({ side }) {
  const color = side === "back" ? "#45667e" : "#173c5b";
  return <g className={`gym-run-leg-${side}`}>
    <g className="gym-run-thigh" style={{ transformOrigin: "163px 147px" }}>
      <path d="M163 147v26" stroke={color} strokeWidth="12" strokeLinecap="round" />
      <g className="gym-run-shin" style={{ transformOrigin: "163px 173px" }}>
        <path d="M163 173v26" stroke={color} strokeWidth="11" strokeLinecap="round" />
        <g className="gym-run-foot" style={{ transformOrigin: "163px 199px" }}>
          <path d="M158 194h9l3 5 9 2q3 1 2 4h-24q-2-5 1-11Z" fill={side === "back" ? "#8eafbc" : "#087e93"} />
          <path d="M157 204h24" stroke="#eaf2f5" strokeWidth="2" strokeLinecap="round" />
        </g>
      </g>
    </g>
  </g>;
}

function RunnerArm({ side, x }) {
  const color = side === "back" ? "#c78d6e" : "#dba686";
  return <g className={`gym-run-arm-${side}`}>
    <g className="gym-run-upper-arm" style={{ transformOrigin: `${x}px 119px` }}>
      <path d={`M${x} 119v22`} stroke={color} strokeWidth="8" strokeLinecap="round" />
      <g className="gym-run-forearm" style={{ transformOrigin: `${x}px 141px` }}>
        <path d={`M${x} 141v19`} stroke={color} strokeWidth="7" strokeLinecap="round" />
        <circle cx={x} cy="162" r="4.5" fill={color} />
      </g>
    </g>
  </g>;
}

function CurlArm({ side, x, shoulder }) {
  return <g className={`gym-curl-${side}`}>
    <path d={`M${shoulder} 121 ${x} 146`} stroke="#ae7a5c" strokeWidth="10" strokeLinecap="round" />
    <g className="gym-curl-forearm" style={{ transformOrigin: `${x}px 146px` }}>
      <path d={`M${x} 146v23`} stroke="#ae7a5c" strokeWidth="9" strokeLinecap="round" />
      <g className="gym-curl-weight" style={{ transformOrigin: `${x}px 169px` }}>
        <path d={`M${x-14} 169h28`} stroke="#385a6f" strokeWidth="5" strokeLinecap="round" />
        <rect x={x-16} y="161" width="6" height="16" rx="2" fill="#25465e" />
        <rect x={x+10} y="161" width="6" height="16" rx="2" fill="#25465e" />
        <circle cx={x} cy="169" r="4.5" fill="#bd8a6c" />
      </g>
    </g>
  </g>;
}

export default function GymActivityScene() {
  const sceneRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(sceneRef.current);
    return () => observer.disconnect();
  }, []);
  return <section ref={sceneRef} className={`gym-studio${inView ? " is-playing" : ""}`} aria-label="Gym illustration">
    <div className="gym-studio-copy"><span className="gym-eyebrow">FIND YOUR RHYTHM</span><h2>A little movement. <br />A stronger you.</h2><p>On the gym floor. On the way home. <br />Every step is part of your story.</p></div>
    <svg className="gym-studio-scene" viewBox="0 0 800 260" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="gym-window" x2="0" y2="1"><stop stopColor="#bde7ed"/><stop offset="1" stopColor="#f0faf7"/></linearGradient>
        <linearGradient id="gym-floor" x2="0" y2="1"><stop stopColor="#e6eff2"/><stop offset="1" stopColor="#f7fafb"/></linearGradient>
      </defs>
      <path d="M15 220 95 176h626l65 44-49 31H66Z" fill="url(#gym-floor)"/>
      <g stroke="#d5e3e9" strokeWidth="1"><path d="m94 202 631 0M56 224h706M168 181l-20 66M326 181l-8 70M484 181l12 70M642 181l25 65"/></g>
      <path d="M84 182V64a39 39 0 0 1 78 0v118" fill="url(#gym-window)" stroke="#d2e6ed" strokeWidth="5"/>
      <path d="M182 182V64a39 39 0 0 1 78 0v118" fill="url(#gym-window)" stroke="#d2e6ed" strokeWidth="5"/>
      <path d="M87 103h73m-36-74v152M185 103h73m-36-74v152" stroke="white" strokeWidth="4"/>
      <rect x="358" y="33" width="132" height="123" rx="24" fill="#edf0fa" stroke="#dbe2ee" strokeWidth="3"/>
      <path d="m380 130 68-74m-37 87 49-52" stroke="white" strokeWidth="10" opacity=".75"/>
      <rect x="576" y="41" width="117" height="53" rx="12" fill="#173c5b"/>
      <path className="gym-pulse-line" d="M591 69h18l7-13 10 25 9-19 7 7h35" fill="none" stroke="#9fdfd5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <g fill="#668197" fontSize="10" fontFamily="sans-serif" fontWeight="700" letterSpacing="2"><text x="126" y="246" textAnchor="middle">MOVE</text><text x="417" y="246" textAnchor="middle">LIFT</text><text x="658" y="246" textAnchor="middle">STRETCH</text></g>
      <ellipse cx="173" cy="217" rx="92" ry="10" fill="#173c5b" opacity=".08"/>
      <g stroke="#244862" strokeLinecap="round" strokeLinejoin="round">
        <path d="m105 211 142 0-18-13H112Z" fill="#254962" strokeWidth="4"/>
        <path d="M121 198h102" stroke="#87baca" strokeWidth="5"/>
        <path d="m224 199-12-65-22-7" fill="none" strokeWidth="7"/>
        <path d="m207 128 18-10 12 10-18 10Z" fill="#8dd6d4" strokeWidth="3"/>
      </g>
      <g className="gym-runner">
        <RunnerArm side="back" x={155} />
        <RunnerLeg side="back" />
        <RunnerLeg side="front" />
        <path d="m157 110 17 6-7 34h-18Z" fill="#098da3" />
        <path d="m163 111 4-15" stroke="#d79f7e" strokeWidth="10" strokeLinecap="round" />
        <circle cx="169" cy="88" r="14" fill="#dba686" />
        <path d="M155 87q-1-22 18-16 16 3 11 16l-7-7-9 5-9-1Z" fill="#233948" />
        <RunnerArm side="front" x={164} />
      </g>
      <ellipse cx="418" cy="216" rx="60" ry="9" fill="#173c5b" opacity=".08"/>
      <path d="m401 165-5 42m31-42 8 42" stroke="#284559" strokeWidth="14" strokeLinecap="round"/>
      <path d="m388 210 16 0m24 0h18" stroke="#718fa4" strokeWidth="10" strokeLinecap="round"/>
      <path d="m399 114 29 0 7 49q-18 11-43 0Z" fill="#8874c5"/>
      <path d="M413 116v-15" stroke="#9d6c51" strokeWidth="11"/>
      <circle cx="413" cy="87" r="15" fill="#ae7a5c"/>
      <path d="M397 87q-4-19 14-20 23 0 18 23l-9-8-11-2-10 10Z" fill="#263745"/>
      <CurlArm side="left" x={390} shoulder={398} />
      <CurlArm side="right" x={438} shoulder={430} />
      <rect x="594" y="207" width="123" height="10" rx="5" fill="#a5d9cc" />
      <ellipse cx="653" cy="216" rx="46" ry="6" fill="#173c5b" opacity=".08" />
      <path d="m644 160-9 48m24-48 11 48" stroke="#284b60" strokeWidth="13" strokeLinecap="round" />
      <path d="M628 210h15m20 0h15" stroke="#538c84" strokeWidth="9" strokeLinecap="round" />
      <g className="gym-stretch-torso" style={{ transformOrigin: "652px 159px" }}>
        <path d="m640 115 24 0 6 45q-19 8-36 0Z" fill="#d6a756" />
        <path d="M652 116v-14" stroke="#b97550" strokeWidth="10" />
        <circle cx="652" cy="91" r="14" fill="#c3845d" />
        <path d="M637 94q-6-27 14-22 19-3 17 23l-10-10-14 5Z" fill="#283b43" />
        <circle cx="665" cy="70" r="7" fill="#283b43" />
        <path d="M640 120 631 94 648 69m16 51 9-26-17-25" stroke="#c3845d" strokeWidth="8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M648 69h8" stroke="#c3845d" strokeWidth="9" strokeLinecap="round" />
      </g>
      <path d="M749 190h21l-4 24h-14Z" fill="#acc9d5"/><path d="M759 192v-49m0 28q-27-18-15-31 17 12 15 31m0 5q25-17 22-31-21 5-22 31" fill="#6ca699" stroke="#6ca699" strokeWidth="3"/>
    </svg>
  </section>;
}
